import Image from "next/image";
import dynamic from 'next/dynamic';
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import Link from "next/link";

const AnimatedSection = dynamic(() => import('../../components/Animations/useAnimations'), { ssr: false });
const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});

interface RevisionRhinoplastyData {
  heading?: {
    title?: string;
  };
  heading2?: {
    title?: string;
  };
  text?: {
    text?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  images?: {
    images_id: {
      alt: string;
      image_file: {
        image: {
          id: string;
          filename_download: string;
        };
      };
    };
  }[];
}

export const fields = [
  // headings
  "heading.title",
  "heading2.title",

  // text
  "text.text",

  // button
  "button.label",
  "button.href",

  // images
  "images.images_id.alt",
  "images.images_id.image_file.image.id",
  "images.images_id.image_file.image.filename_download",
] as const;

interface RevisionRhinoplastyProps {
  zIndex: number;
  thresholds: number[][];
  transitionTime?: number;
  mobileThresholds: number[][];
  helperRotation?: number;
  data?: RevisionRhinoplastyData;
}

export default function RevisionRhinoplasty({
  zIndex,
  thresholds,
  transitionTime,
  mobileThresholds,
  helperRotation,
  data
}: RevisionRhinoplastyProps) {
  // Get the first image for the small image
  const smallImage = data?.images?.[0];
  // Get the second image for the main image
  const mainImage = data?.images?.[1];

  return (
    <ScreenAwareContent
	    supportedContent={
        <AnimatedSection
          helperRotation={helperRotation}
          className="h-screen overflow-hidden fixed w-full pt-16 bg-white lg:pt-32  top-0 left-0"
          thresholds={[
            thresholds?.[0]?.[0],
            thresholds?.[thresholds.length - 1]?.[1],
          ]}
          mobileThresholds={[
            mobileThresholds?.[0]?.[0],
            mobileThresholds?.[mobileThresholds.length - 1]?.[1],
          ]}
          zIndex={zIndex}
          withZIndex={true}
          transitionTime={transitionTime}
          variants={{
            desktop: {
              visible: { x: 0, y: 0, opacity: 1 },
              above: { x: "100vw", y: 0, opacity: 1 },
              below: { x: 0, y: "-100vh", opacity: 1 },
            },
            mobile: {
              visible: { x: 0, opacity: 1 },
              above: { x: "100vw", opacity: 1 },
              below: { x: "-100vw", opacity: 1 },
            },
          }}
        >
          <div className="flex flex-col h-full w-full lg:flex-row lg:w-[100vw] overflow-hidden">
            <div className="flex flex-col h-full lg:flex-row">
              {/* Title */}
              <div className="w-full px-6 pt-8 lg:w-[20vw] lg:h-full hidden lg:flex lg:flex-col lg:items-center lg:justify-start lg:pt-20">
                <div className="max-w-80 flex-col justify-start items-start gap-6 lg:gap-8 inline-flex gl-desktop-scale">
                  <div className="h-48 w-full justify-center items-center inline-flex lg:h-60">
                    <div className="relative w-full h-full">
                      {smallImage && (
                        <Image
                          src={smallImage?.images_id?.image_file?.image?.id && smallImage?.images_id?.image_file?.image?.filename_download ? getReadableImageUrl(
                            smallImage.images_id.image_file.image.id,
                            smallImage.images_id.image_file.image.filename_download
                          ) : "/images/award.png"}
                          alt={smallImage.images_id.alt}
                          width={320}
                          height={240}
                          className="object-contain object-center w-full h-full"
                          priority
                          quality={90}
                        />
                      )}
                    </div>
                  </div>
                  <div className="self-stretch text-primary text-4xl font-light font-essonnes text-center xl:text-5xl xl:text-left">
                    {data?.heading?.title}
                  </div>
                </div>
              </div>

              {/* Image */}
              <div className="w-full h-48 lg:w-[35vw] xl:w-[40vw] lg:h-full lg:mt-0 flex items-center justify-center shrink-0">
                <AnimatedSection 
                      section="div"
                      mobileThresholds={mobileThresholds[1]}
                      className="relative w-full h-full z-10"
                      variants={{
                        desktop: {
                          visible: { scale: 1, y: 0, x: 0, opacity: 1 },
                          above: { scale: 1, y: 0, x: 0, opacity: 1 },
                          below: { scale: 1, y: 0, x: 0, opacity: 1 },
                        },
                        mobile: {
                          visible: { scale: 1, y: 0, x: 0, opacity: 1, zIndex: 20 },
                          above: { scale: 4.5, y: 300, x: "-10%", opacity: 1, zIndex: 20 },
                          below: { scale: 1, y: 0, x: 0, opacity: 1, zIndex: 20 },
                        }
                      }}
                      withZIndex={true}
                    >
                  {mainImage && (
                    <Image
                      src={mainImage?.images_id?.image_file?.image?.id && mainImage?.images_id?.image_file?.image?.filename_download ? getReadableImageUrl(
                        mainImage.images_id.image_file.image.id,
                        mainImage.images_id.image_file.image.filename_download
                      ) : "/images/award.png"}
                      alt={mainImage.images_id.alt}
                      fill
                      className="object-cover object-center xl:object-top"
                      priority
                      quality={100}
                    />
                  )}
                </AnimatedSection>
              </div>

              {/* Content */}
              <div className="w-full px-6 lg:w-[45vw]  xl:w-[40vw] lg:h-full mt-4 lg:mt-0 flex items-center justify-center shrink-0">
                <AnimatedSection
                  section="div"
                  thresholds={[
                    thresholds?.[0]?.[0],
                    thresholds?.[thresholds.length - 1]?.[1],
                  ]}
                  mobileThresholds={mobileThresholds[1]}
                  zIndex={zIndex}
                  transitionTime={transitionTime}
                  className="flex flex-col justify-start items-start gap-8 xl:gap-12 xl:pr-10"
                  variants={{
                    desktop: {
                      visible: { x: 0, opacity: 1 },
                      above: { x: "100vw", opacity: 1 },
                      below: { x: 0, opacity: 1 },
                    },
                    mobile: {
                      visible: { x: 0, opacity: 1 },
                      above: { x: "100vw", opacity: 1 },
                      below: { x: 0, opacity: 1 },
                    }
                  }}
                >
                  <div className="w-full gl-desktop-scale flex flex-col gap-2 xl:gap-0 text-sm xl:text-base">
                    <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] pb-2 text-primary xl:pb-6">
                      {data?.heading2?.title}
                    </h2>

                    <div className="flex justify-start items-start overflow-hidden xl:pr-10 xl:pb-6">
                      <div
                        className="w-full flex flex-col justify-start items-start space-y-4 md:gap-2 xl:gap-8"
                        dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                      />
                    </div>

                    <div className="w-full flex justify-center xl:justify-start pt-4">
                      {data?.button && (
                        <Link href={data.button.href || "#"} className="h-14 px-8 py-3 bg-primary rounded-full flex justify-center items-center gap-4 xl:h-16 xl:px-9">
                          <span className="text-white text-lg font-normal font-greycliff leading-[1] tracking-tight xl:text-xl">
                            {data.button.label}
                          </span>
                        </Link>
                      )}
                    </div>
                  </div>
                </AnimatedSection>
              </div>
            </div>
          </div>
        </AnimatedSection>
      }
      fallbackContent={
        <section className="overflow-hidden w-full h-full">
          <div className="flex flex-col h-full w-full xl:flex-row xl:w-[100vw] overflow-hidden">
            <div className="flex flex-col h-full xl:flex-row">
              {/* Title */}
              <div className="w-full px-6 pt-8 xl:w-[20vw] xl:h-full hidden xl:flex xl:flex-col xl:items-center xl:justify-start xl:pt-20">
                <div className="max-w-80 flex-col justify-start items-start gap-6 xl:gap-8 inline-flex gl-desktop-scale">
                  <div className="h-48 w-full justify-center items-center inline-flex xl:h-60">
                    <div className="relative w-full h-full">
                      {smallImage && (
                        <Image
                          src={smallImage?.images_id?.image_file?.image?.id && smallImage?.images_id?.image_file?.image?.filename_download ? getReadableImageUrl(
                            smallImage.images_id.image_file.image.id,
                            smallImage.images_id.image_file.image.filename_download
                          ) : "/images/award.png"}
                          alt={smallImage.images_id.alt}
                          width={320}
                          height={240}
                          className="object-contain object-center w-full h-full"
                          priority
                          quality={90}
                        />
                      )}
                    </div>
                  </div>
                  <div className="self-stretch text-primary text-4xl font-light font-essonnes text-center xl:text-5xl xl:text-left">
                    {data?.heading?.title}
                  </div>
                </div>
              </div>

              {/* Image */}
              <div className="w-full h-48 xl:w-[40vw] xl:h-screen xl:mt-0 flex items-center justify-center shrink-0">
                <div className="relative w-full h-full z-10 overflow-hidden">
                  {mainImage && (
                    <Image
                      src={mainImage?.images_id?.image_file?.image?.id && mainImage?.images_id?.image_file?.image?.filename_download ? getReadableImageUrl(
                        mainImage.images_id.image_file.image.id,
                        mainImage.images_id.image_file.image.filename_download
                      ) : "/images/award.png"}
                      alt={mainImage.images_id.alt}
                      fill
                      className="object-cover object-center xl:object-top"
                      priority
                      quality={100}
                    />
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="w-full px-8 py-8 xl:w-[40vw] xl:h-full flex items-center justify-center shrink-0">
                <div className="flex flex-col justify-start items-start gap-8 xl:gap-12 xl:px-10">
                  <div className="w-full gl-desktop-scale flex flex-col gap-2 xl:gap-0 text-sm xl:text-base">
                    <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] pb-2 text-primary xl:pb-6">
                      {data?.heading2?.title}
                    </h2>

                    <div className="flex justify-start items-start overflow-hidden xl:pr-10 xl:pb-6">
                      <div
                        className="w-full flex flex-col justify-start items-start space-y-4 md:gap-2 xl:gap-8"
                        dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                      />
                    </div>

                    <div className="w-full flex justify-center xl:justify-start pt-4">
                      {data?.button && (
                        <Link href={data.button.href || "#"} className="h-14 px-8 py-3 bg-primary rounded-full flex justify-center items-center gap-4 xl:h-16 xl:px-9">
                          <span className="text-white text-lg font-normal font-greycliff leading-[1] tracking-tight xl:text-xl">
                            {data.button.label}
                          </span>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      }
    />
  );
}
